// @flow
import type { ActionType } from 'redux-actions';
import { call, select, put, takeLatest, takeEvery } from 'redux-saga/effects';
import api from './brandEngageSearchApi';
import { selectToken } from 'ducks/SagaUtils';
import Product from 'models/Product';
import UGC from 'models/Ugc';
import {
  requestBrandEngageAddResponse,
  receiveBrandEngageAddResponse,
  requestBrandEngageSearchResults,
  receiveBrandEngageSearchResults,
  requestBrandEngageDismissContent,
  receiveBrandEngageDismissContent,
  removeDismissedItem,
} from './brandEngageActions';
import { postObservations, postResponse } from 'ducks/coreDataApi';
import { delay } from 'redux-saga';
import { boundedDelay } from 'ducks/SagaUtils';
import { Map, Set } from 'immutable';

export function* deleteDismissedContentTask(
  action: ActionType<typeof receiveBrandEngageDismissContent>
): Generator<*, *, *> {
  const { error, meta: ugc_id } = action;

  if (error === true) {
    return;
  }

  yield call(delay, 500);
  yield put(removeDismissedItem(ugc_id));
}

export function* dismissBrandEngageTask(
  action: ActionType<typeof requestBrandEngageDismissContent>
): Generator<*, *, *> {
  const { payload } = action;
  const token = yield select(selectToken);
  const startTs = Date.now();
  let actionToPut;

  try {
    const response = yield call(postObservations, token, {
      ugcId: payload.ugc_id,
      body: payload.observationPayload,
      clientId: payload.clientId,
    });
    actionToPut = receiveBrandEngageDismissContent(response, payload.ugc_id);
  } catch (error) {
    console.error(error);
    actionToPut = receiveBrandEngageDismissContent(error, payload.ugc_id);
  } finally {
    yield call(delay, boundedDelay(startTs));
    yield put(actionToPut);
  }
}

export function* respondBrandEngageTask(
  action: ActionType<typeof requestBrandEngageAddResponse>
): Generator<*, *, *> {
  const { payload } = action;
  const token = yield select(selectToken);
  const startTs = Date.now();
  let actionToPut;

  try {
    const response = yield call(postResponse, token, payload);
    actionToPut = receiveBrandEngageAddResponse(
      UGC.createWithData({ ugc: response }),
      payload.parent_ugc_id
    );
  } catch (error) {
    console.error(error);
    actionToPut = receiveBrandEngageAddResponse(error, payload.parent_ugc_id);
  } finally {
    yield call(delay, boundedDelay(startTs));
    yield put(actionToPut);
  }
}

function* searchSaga(
  action: ActionType<typeof requestBrandEngageSearchResults>
) {
  const token = yield select(selectToken);
  const { clientId, channelName, brandEngageType } = action.payload;

  try {
    const rawResults = yield call(api, token, {
      brandEngageType,
      clientId,
      channelName,
    });

    const { ids, itemsById } = rawResults.reduce(
      (acc, item) => {
        const ugc = UGC.createWithData(item);
        const ugcId = ugc.ugc_id;

        acc.ids.push(ugcId);
        acc.itemsById = acc.itemsById.set(ugcId, {
          subject: Product.createWithData(item),
          ugc,
          observations: Set(
            item.existing_observations.map(ob => ob.observation_type_code)
          ),
          brand_catalog_id: item.brand_catalog.catalog_id,
        });

        return acc;
      },
      {
        ids: [],
        itemsById: Map(),
      }
    );

    yield put(
      receiveBrandEngageSearchResults([ids, itemsById], action.payload)
    );
  } catch (err) {
    yield put(receiveBrandEngageSearchResults(err, action.payload));
  }
}

export default function* brandEngageSaga(): Generator<*, *, *> {
  yield takeLatest(requestBrandEngageSearchResults, searchSaga);
  yield takeEvery(requestBrandEngageAddResponse, respondBrandEngageTask);
  yield takeEvery(requestBrandEngageDismissContent, dismissBrandEngageTask);
  yield takeEvery(receiveBrandEngageDismissContent, deleteDismissedContentTask);
}



// WEBPACK FOOTER //
// ./src/containers/brand-engage/brandEngageSaga.js